import { HierarcicalObjectReference } from "@novorender/data-js-api";

import { Node, Tree } from "components/objectTree/types";
import { getParentPaths } from "components/objectTree/utils";
import { ModelTreeNodeData } from "features/modelTree/types";
import { NodeType } from "types/misc";

/**
 * Creates a tree node. The only difference between regular and parent nodes is the expanded state.
 *
 * @param path - The object path
 * @param obj - Optional hierarchical object reference (undefined for parent-only nodes)
 * @param isParent - If true, node is auto-expanded (for path hierarchy that didn't match search)
 *                   If false, node starts collapsed (for actual search results)
 */
function createNode(path: string, obj?: HierarcicalObjectReference, isParent = false): Node<ModelTreeNodeData> {
    const pathParts = path.split("/");
    const name = pathParts[pathParts.length - 1];

    const isLeaf = obj ? obj.type === NodeType.Leaf : false;
    const expanded = isParent;

    return {
        path: obj?.path ?? path,
        name,
        expanded,
        loading: false,
        isLeaf,
        children: isLeaf ? undefined : [],
        data: {
            object:
                obj ??
                ({
                    id: -1,
                    path,
                    type: NodeType.Internal,
                } as HierarcicalObjectReference),
        },
    };
}

export function createNodeFromObject(obj: HierarcicalObjectReference): Node<ModelTreeNodeData> {
    return createNode(obj.path, obj, false);
}

export function createParentNode(path: string): Node<ModelTreeNodeData> {
    return createNode(path, undefined, true);
}

/**
 * Builds a filtered tree structure from database search results.
 *
 * This function is necessary because the ModelTree uses lazy loading - unexpanded folders
 * don't have their children in memory. To search ALL objects (not just visible ones),
 * we must query the database directly and build a new tree from the results.
 *
 * Note: We cannot use filterOutObjectsNotOnPath() from modelTree/utils.ts because it only
 * filters nodes that already exist in the tree. Since the tree is lazy-loaded, most nodes
 * don't exist in memory until explicitly expanded by the user.
 *
 * @param matchingObjects - Array of objects from database search results
 * @returns A new tree containing only the matching objects and their parent hierarchy
 */
export function buildFilteredTree(matchingObjects: HierarcicalObjectReference[]): Tree<ModelTreeNodeData> {
    const filteredTree: Tree<ModelTreeNodeData> = {
        "": {
            path: "",
            name: "Root",
            expanded: true,
            loading: false,
            isLeaf: false,
            children: [],
            data: {
                object: { id: -1, path: "", type: NodeType.Internal } as HierarcicalObjectReference,
            },
        },
    };

    if (matchingObjects.length === 0) {
        return filteredTree;
    }

    const pathsToCreate = new Set<string>();

    // Create nodes for all matching objects and collect all parent paths
    for (const obj of matchingObjects) {
        filteredTree[obj.path] = createNodeFromObject(obj);

        for (const parentPath of getParentPaths(obj.path, false)) {
            pathsToCreate.add(parentPath);
        }
    }

    // Create parent nodes for all parent paths
    for (const path of pathsToCreate) {
        if (!filteredTree[path]) {
            filteredTree[path] = createParentNode(path);
        } else if (!filteredTree[path].isLeaf && !filteredTree[path].expanded) {
            // Node already exists as a search result but also serves as a parent of other
            // matching results — expand it so its matching children are visible.
            filteredTree[path] = { ...filteredTree[path], expanded: true };
        }
    }

    const allPaths = [...pathsToCreate, ...matchingObjects.map((obj) => obj.path)];

    // Link children to parents
    for (const path of allPaths) {
        const pathParts = path.split("/");
        const parentPath = pathParts.slice(0, -1).join("/");

        const parent = filteredTree[parentPath];
        if (parent && parent.children && !parent.children.includes(path)) {
            parent.children.push(path);
        }
    }

    return filteredTree;
}
